import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchGoldPrice,
  fetchGoldPriceHistory,
  GOLD_HISTORY_DATA_KEY,
  GOLD_PRICE_DATA_KEY
} from "../api/gold-price/gold-price.api.ts";
import { useLocation } from "react-router-dom";
import { emptyHistoryGoldData } from "../data/goldPriceData.js";

const getUrlParams = (search) => {
  const urlParams = new URLSearchParams(search);
  return {
    day: urlParams.get("gold_history_day") || "",
    month: urlParams.get("gold_history_month") || "",
    year: urlParams.get("gold_history_year") || ""
  };
};

const GoldPriceTable = () => {
  const location = useLocation();
  const { day, month, year } = getUrlParams(location.search);
  const isHistoryMode = !!(day && month && year);

  // Query for current gold price
  const {
    data: goldDataRes,
    isLoading: goldDataLoading,
    error: goldDataError
  } = useQuery({
    queryKey: [GOLD_PRICE_DATA_KEY],
    queryFn: fetchGoldPrice,
    enabled: true
  });

  // Query for gold price history (only if history page)
  const {
    data: historyDataRes,
    isLoading: historyGoldDataLoading,
    error: historyError
  } = useQuery({
    queryKey: [GOLD_HISTORY_DATA_KEY, day, month, year],
    queryFn: () => fetchGoldPriceHistory({ date: `${year}${month}${day}` }),
    enabled: isHistoryMode
  });

  const goldData = useMemo(() => {
    if (!goldDataRes || !goldDataRes?.data?.locations?.length) return [];

    return goldDataRes?.data?.locations;
  }, [goldDataRes]);

  const historyData = useMemo(() => {
    if (!historyDataRes || !historyDataRes?.data?.locations?.length) return [];

    return historyDataRes?.data?.locations;
  }, [historyDataRes]);

  const updateTime = goldDataRes?.data
    ? goldDataRes?.data?.updated_text
    : goldDataRes
    ? goldDataRes?.updated_text
    : "";

  const tableRows = useMemo(() => {
    if (!goldData || goldData.length === 0) return null;
    return goldData.map((location, idx) => {
      const hasPNJ = location.gold_type?.some((type) => type.name === "PNJ");
      const hasSJC = location.gold_type?.some((type) => type.name === "SJC");
      return location.gold_type?.map((type, jdx) => {
        let tdClass;
        if (hasPNJ && hasSJC) {
          tdClass =
            type.name === "PNJ"
              ? "bg-[#f7c710] text-center align-middle border border-gray-400 h-[20px] leading-[40px]"
              : "bg-[#F8E603] text-center align-middle border border-gray-400 h-[20px] leading-[40px]";
        } else {
          tdClass =
            jdx % 2 === 0
              ? "bg-[#f7c710] text-center align-middle border border-gray-400 h-[20px] leading-[40px]"
              : "bg-[#F8E603] text-center align-middle border border-gray-400 h-[20px] leading-[40px]";
        }
        return (
          <tr key={location.name + "-" + type.name}>
            {jdx === 0 && (
              <td
                rowSpan={location.gold_type.length}
                className="bg-[#fff000] text-center align-middle border border-gray-400 h-[20px] leading-[40px]"
              >
                {location.name}
              </td>
            )}
            <td className={tdClass}>{type.name}</td>
            <td className="text-center align-middle border border-gray-400">
              {type.gia_mua || (type.data && type.data[0]?.gia_mua) || "--"}
            </td>
            <td className="text-center align-middle border border-gray-400">
              {type.gia_ban || (type.data && type.data[0]?.gia_ban) || "--"}
            </td>
            {/* <td className="text-center align-middle border border-gray-400">
              {type.updated_at ||
                (type.data && type.data[0]?.updated_at) ||
                "--"}
            </td> */}
          </tr>
        );
      });
    });
  }, [goldData]);

  return (
    <div>
      <div>
        <section className="portlet mb-2 relative">
          <div className="portlet-content">
            <h2 className="hidden">View Current Gold Prices Portlet</h2>
            <div className="portlet-content-container">
              <div className="portlet-body">
                {/* Header bảng giá vàng */}
                <div className="text-center mb-[5px] bg-white py-8 border border-gray-500">
                  <span className="block text-[18px] uppercase text-[#f7c710]">
                    Bảng giá vàng
                  </span>
                  <div
                    className="text-[#000] text-[13px] block box-border"
                    id="update-time-display"
                  >
                    {goldDataLoading
                      ? "Đang tải dữ liệu..."
                      : goldDataError
                      ? ""
                      : updateTime}
                  </div>
                  <div className="text-[#000] text-[13px] block box-border">
                    (Đơn vị: ngàn đồng/lượng)
                  </div>
                </div>
                {/* Table */}
                <table
                  className="w-full border-collapse border border-gray-400 md:text-[12px] font-sans"
                  id="gold-price-table"
                >
                  <thead>
                    <tr>
                      <th className="bg-[#193a66] text-white font-bold text-center border border-gray-400 md:text-[12px] h-[20px] leading-[40px]">
                        Khu vực
                      </th>
                      <th className="bg-[#193a66] text-white font-bold text-center border border-gray-400 md:text-[12px] h-[20px] leading-[40px]">
                        Loại vàng
                      </th>
                      <th className="bg-[#193a66] text-white font-bold text-center border border-gray-400 md:text-[12px] h-[20px] leading-[40px]">
                        Giá mua
                      </th>
                      <th className="bg-[#193a66] text-white font-bold text-center border border-gray-400 md:text-[12px] h-[20px] leading-[40px]">
                        Giá bán
                      </th>
                      {/* <th className="bg-[#193a66] text-white font-bold text-center border border-gray-400 md:text-[12px] h-[20px] leading-[40px]">
                        Thời gian cập nhật
                      </th> */}
                    </tr>
                  </thead>
                  <tbody id="gold-price-data" className="text-[14px]">
                    {goldDataLoading ? (
                      <tr>
                        <td colSpan={5} className="text-center py-4">
                          Đang tải dữ liệu...
                        </td>
                      </tr>
                    ) : goldDataError ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="text-center py-4 text-red-600"
                        >
                          Không thể tải dữ liệu giá vàng.
                        </td>
                      </tr>
                    ) : tableRows && tableRows.length > 0 ? (
                      tableRows
                    ) : (
                      <tr>
                        <td colSpan={5} className="text-center py-4">
                          Không có dữ liệu.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>
      {/* Search Results Section */}
      {isHistoryMode &&
        (historyGoldDataLoading ? (
          <div className="text-[#000] text-[11px] block box-border text-center self-center">
            Đang tải dữ liệu...
          </div>
        ) : historyError ? (
          emptyHistoryGoldData.map((location, idx) => (
            <div key={location + idx} className="mb-8">
              <table className="w-full border-collapse border border-gray-400 font-sans">
                <thead>
                  <tr>
                    <th
                      colSpan={4}
                      className="bg-[#193a66] text-white font-bold text-center border border-gray-400 md:text-[12px] h-auto leading-[40px]"
                    >
                      Lịch sử giá vàng {location.name}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="bg-[#fff000] text-center font-bold align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]">
                      Loại vàng
                    </td>
                    <td className="bg-[#fff000] text-center font-bold align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]">
                      Giá mua
                    </td>
                    <td className="bg-[#fff000] text-center font-bold align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]">
                      Giá bán
                    </td>
                    <td className="bg-[#fff000] text-center font-bold align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]">
                      Thời gian cập nhật
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))
        ) : (
          historyData.map((location, idx) => (
            <div key={location.name + idx} className="mb-8">
              <table className="w-full border-collapse border border-gray-400 font-sans">
                <thead>
                  <tr>
                    <th
                      colSpan={4}
                      className="bg-[#193a66] text-white font-bold text-center border border-gray-400 md:text-[12px] h-auto leading-[40px]"
                    >
                      Lịch sử giá vàng {location.name}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="bg-[#fff000] text-center font-bold align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]">
                      Loại vàng
                    </td>
                    <td className="bg-[#fff000] text-center font-bold align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]">
                      Giá mua
                    </td>
                    <td className="bg-[#fff000] text-center font-bold align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]">
                      Giá bán
                    </td>
                    <td className="bg-[#fff000] text-center font-bold align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]">
                      Thời gian cập nhật
                    </td>
                  </tr>
                  {Array.isArray(location.gold_type) &&
                  location.gold_type.length > 0 ? (
                    location.gold_type.map((type, jdx) =>
                      (type.data || []).map((history, hdx) => (
                        <tr key={type.name + "-" + hdx}>
                          {hdx === 0 && (
                            <td
                              rowSpan={type.data.length}
                              className="text-center align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]"
                            >
                              {type.name}
                            </td>
                          )}
                          {hdx !== 0 && null}
                          <td className="text-center align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]">
                            {history.gia_mua || "--"}
                          </td>
                          <td className="text-center align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]">
                            {history.gia_ban || "--"}
                          </td>
                          <td className="text-center align-middle border border-gray-400 text-[14px] h-[20px] leading-[40px]">
                            {history.updated_at || "--"}
                          </td>
                        </tr>
                      ))
                    )
                  ) : (
                    <></>
                  )}
                </tbody>
              </table>
            </div>
          ))
        ))}
    </div>
  );
};

export default GoldPriceTable;
